import { Button, FormControl, Grid, TextField, Typography } from '@mui/material';
import React, { useState } from 'react';
import Modal from '../../../components/common/Modal';
import { MuiTelInput } from 'mui-tel-input';
import UserService from '../../../services/user.service';
import {
  CONTACT_METHOD_TYPE,
  CONTACT_METHOD_VERIFICATION_TYPE,
} from 'vendor-marketplace-shared-library';
import { FAILED_TO_CREATE_AUTH_CHALLENGE, UPDATE_USER } from '../../../error-messages';
import useAlert from '../../../hooks/useAlert';
import useAuth from '../../../hooks/useAuth';

type ContactMethodUpdateModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  contactMethodType: string;
  refetchData: () => void;
};

export default function ContactMethodUpdateModal(props: ContactMethodUpdateModalProps) {
  const { setAlert, clearAlert } = useAlert();
  const { userQuery } = useAuth();
  const user = userQuery?.data?.me;

  const [phone, setPhone] = useState(user.phoneContactMethod?.value || '');
  const [email, setEmail] = useState(user.emailContactMethod?.value || '');
  const [contactMethodVerificationId, setContactMethodVerificationId] = useState<number>(0);
  const [openVerificationModal, setOpenVerificationModal] = useState(false);
  const [code, setCode] = useState('');

  const createAuthChallenge = async () => {
    clearAlert();

    try {
      const response = await UserService.createAuthChallenge(
        CONTACT_METHOD_VERIFICATION_TYPE.UPDATE,
        props.contactMethodType,
        props.contactMethodType === CONTACT_METHOD_TYPE.PHONE ? phone : email,
      );
      setContactMethodVerificationId(response.data.contactMethodVerificationId);
      setOpenVerificationModal(true);
    } catch (error: any) {
      setAlert({
        message: error.response?.data?.error || FAILED_TO_CREATE_AUTH_CHALLENGE,
        severity: 'error',
      });
    }
  };

  const verifyAuthChallenge = async () => {
    if (contactMethodVerificationId) {
      try {
        await UserService.verifyAuthChallenge(
          CONTACT_METHOD_VERIFICATION_TYPE.UPDATE,
          contactMethodVerificationId,
          code,
        );
        props.setOpen(false);
        setOpenVerificationModal(false);
        await userQuery.refetch();
      } catch (error: any) {
        setAlert({
          message: error.response?.data?.error || UPDATE_USER,
          severity: 'error',
        });
      } finally {
        props.refetchData();
      }
    }
  };

  return (
    <Modal open={props.open} setOpen={props.setOpen} title={'Update Contact Method'}>
      <Grid container padding={4}>
        <Grid item>
          {props.contactMethodType === CONTACT_METHOD_TYPE.PHONE ? (
            <MuiTelInput
              fullWidth
              value={phone}
              onChange={setPhone}
              placeholder='Phone Number'
              defaultCountry='US'
            />
          ) : (
            <FormControl>
              <TextField value={email} onChange={e => setEmail(e.target.value)} />
            </FormControl>
          )}
        </Grid>
        <Grid item>
          <Button onClick={createAuthChallenge}>Verify</Button>
        </Grid>
      </Grid>
      {openVerificationModal && (
        <Modal
          setOpen={setOpenVerificationModal}
          open={openVerificationModal}
          title={'Contact Method Verification'}
        >
          <Grid container paddingY={2} spacing={2}>
            <Grid item>
              <Typography>Enter the code sent to your contact method for verification.</Typography>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <TextField
                  label='Code'
                  variant='outlined'
                  value={code}
                  onChange={e => setCode(e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid item>
              <Button variant={'contained'} onClick={verifyAuthChallenge}>
                Submit
              </Button>
            </Grid>
          </Grid>
        </Modal>
      )}
    </Modal>
  );
}
